<template>
  <div>
    <b-row class="align-items-center mb-2">
      <b-col><h1 class="font-weight-bolder">
        Panel CxC / CxP
      </h1></b-col>
    </b-row>

    <b-overlay
      :show="loading"
      opacity="0.1"
    >
      <b-alert
        :show="!!dashboardError"
        variant="danger"
        class="mb-2"
      >
        {{ dashboardError }}
      </b-alert>

      <b-row class="mb-2">
        <b-col md="4">
          <b-card class="text-center">
            <div class="text-muted small">
              CUENTAS POR PAGAR
            </div>
            <h3>{{ money(payables.total) }}</h3>
          </b-card>
        </b-col>

        <b-col md="4">
          <b-card class="text-center">
            <div class="text-muted small">
              CUENTAS POR COBRAR
            </div>
            <h3>{{ money(receivables.total) }}</h3>
          </b-card>
        </b-col>

        <b-col md="4">
          <b-card
            :class="netClass"
            class="text-center"
          >
            <div :class="netLabelClass">
              NETO (CxC - CxP)
            </div>
            <h3>{{ money(net) }}</h3>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          md="6"
          class="mb-2"
        >
          <b-card no-body>
            <b-card-header class="d-flex justify-content-between align-items-center">
              <span>Proveedores (CxP)</span>
              <b-button
                size="sm"
                variant="success"
                @click="exportCsv('payables')"
              >
                Exportar CSV
              </b-button>
            </b-card-header>
            <b-table
              :items="payables.rows"
              :fields="payablesFields"
              small
              striped
              hover
              empty-text="Sin datos"
            />
          </b-card>
        </b-col>

        <b-col
          md="6"
          class="mb-2"
        >
          <b-card no-body>
            <b-card-header class="d-flex justify-content-between align-items-center">
              <span>Clientes (CxC)</span>
              <b-button
                size="sm"
                variant="success"
                @click="exportCsv('receivables')"
              >
                Exportar CSV
              </b-button>
            </b-card-header>
            <b-table
              :items="receivables.rows"
              :fields="receivablesFields"
              small
              striped
              hover
              empty-text="Sin datos"
            />
          </b-card>
        </b-col>
      </b-row>

      <!-- === Línea: antigüedad del vencido y semanas por vencer === -->
      <b-row>
        <b-col class="mb-2">
          <b-card>
            <apexchart
              v-if="columns.length"
              :key="lineKey"
              type="line"
              height="320"
              :options="lineOptions"
              :series="lineSeries"
            />
            <div
              v-else
              class="text-center text-muted py-4"
            >
              Selecciona un mes válido para ver el resumen.
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- === Vencido por antigüedad + semanas por vencer === -->
      <b-row class="mt-2">
        <b-col>
          <b-card>

            <div class="d-flex align-items-center mb-2">
              <span class="font-weight-bolder">Cartera al {{ asOfLabel }}</span>
              <small class="text-muted ml-2">
                Vencido por días de atraso, y luego semana a semana hasta el último saldo
                con vencimiento.
              </small>
              <b-spinner
                v-if="bucketsLoading"
                small
                class="ml-2"
              />
              <b-button
                size="sm"
                variant="success"
                class="ml-auto"
                :disabled="columns.length === 0"
                @click="exportFridaysCsv"
              >
                Exportar CSV
              </b-button>
            </div>

            <b-alert
              :show="!!bucketsError"
              variant="warning"
              class="mb-2"
            >
              {{ bucketsError }}
            </b-alert>

            <b-alert
              :show="!weekColumns.length && !bucketsError"
              variant="secondary"
              class="mb-2"
            >
              No hay saldos con vencimiento futuro: todo lo que sigue abierto ya venció.
            </b-alert>

            <div class="table-responsive">
              <table class="table table-sm mb-1">
                <thead>
                  <tr>
                    <th
                      rowspan="2"
                      style="min-width:120px"
                    >
                      Concepto
                    </th>
                    <th
                      :colspan="agingColumns.length + 1"
                      class="text-center"
                    >
                      VENCIDO AL {{ asOfLabel }}
                    </th>
                    <th
                      v-if="weekColumns.length"
                      :colspan="weekColumns.length"
                      class="text-center border-left"
                    >
                      POR VENCER
                    </th>
                    <th
                      rowspan="2"
                      class="text-right border-left"
                    >
                      Total
                    </th>
                  </tr>
                  <tr>
                    <th
                      v-for="c in agingColumns"
                      :key="c.key"
                      class="text-right"
                    >
                      {{ c.label }}
                    </th>
                    <th class="text-right bg-light">
                      Subtotal
                    </th>
                    <th
                      v-for="(c, i) in weekColumns"
                      :key="c.key"
                      class="text-right"
                      :class="i === 0 ? 'border-left' : ''"
                    >
                      {{ c.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in bodyRows"
                    :key="row.label"
                  >
                    <td><strong>{{ row.label }}</strong></td>
                    <td
                      v-for="(c, i) in agingColumns"
                      :key="c.key"
                      class="text-right"
                    >
                      {{ money(row.values[i]) }}
                    </td>
                    <td class="text-right bg-light">
                      <strong>{{ money(row.subtotal) }}</strong>
                    </td>
                    <td
                      v-for="(c, i) in weekColumns"
                      :key="c.key"
                      class="text-right"
                      :class="i === 0 ? 'border-left' : ''"
                    >
                      {{ money(row.values[agingColumns.length + i]) }}
                    </td>
                    <td class="text-right">
                      <strong>{{ money(row.total) }}</strong>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr
                    v-for="row in footRows"
                    :key="row.label"
                  >
                    <td><strong>{{ row.label }}</strong></td>
                    <td
                      v-for="(c, i) in agingColumns"
                      :key="c.key"
                      class="text-right"
                    >
                      {{ money(row.values[i]) }}
                    </td>
                    <td class="text-right bg-light">
                      <strong>{{ money(row.subtotal) }}</strong>
                    </td>
                    <td
                      v-for="(c, i) in weekColumns"
                      :key="c.key"
                      class="text-right"
                      :class="i === 0 ? 'border-left' : ''"
                    >
                      {{ money(row.values[agingColumns.length + i]) }}
                    </td>
                    <td class="text-right">
                      <strong>{{ money(row.total) }}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <small class="text-muted d-block">
              <strong>Vencido</strong>: saldos abiertos cuyo vencimiento ya llegó, agrupados por
              días de atraso. <strong>Por vencer</strong>: lo que cae en cada semana, agrupado en
              el viernes de esa semana. El acumulado avanza de izquierda a derecha, del atraso más
              viejo al futuro, y la columna Total cuadra con las tarjetas de arriba.
            </small>
            <small
              v-if="notShownTotal > 0"
              class="text-muted d-block"
            >
              Quedan fuera {{ money(notShownTotal) }} con vencimiento a más de seis meses.
            </small>
            <small
              v-if="noDueDateTotal > 0"
              class="text-muted d-block"
            >
              No se incluyen {{ money(noDueDateTotal) }} sin fecha de vencimiento registrada.
            </small>
          </b-card>
        </b-col>
      </b-row>
    </b-overlay>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { mapActions, mapGetters } from 'vuex'

// De más viejo a más nuevo: el acumulado se lee en orden cronológico
const AGING_COLUMNS = [
  { key: 'd90_plus', label: '+90 días' },
  { key: 'd61_90', label: '61-90 días' },
  { key: 'd31_60', label: '31-60 días' },
  { key: 'd0_30', label: '0-30 días' },
]

const emptyAging = () => ({
  d90_plus: 0, d61_90: 0, d31_60: 0, d0_30: 0, total: 0,
})

export default {
  name: 'FinanceDashboard',
  components: { apexchart: VueApexCharts },
  data() {
    return {
      loading: false,
      bucketsLoading: false,
      dashboardError: null,
      bucketsError: null,
      aging: { cxc: emptyAging(), cxp: emptyAging() },
      upcoming: { cxc: {}, cxp: {} },
      upcomingFridays: [],
      notShownTotal: 0,
      noDueDateTotal: 0,
      asOf: null,
      requestSeq: 0,
      lineKey: 0,
    }
  },
  computed: {
    ...mapGetters('finance', ['payablesDashboard', 'receivablesDashboard']),
    payables() { return this.payablesDashboard || { total: 0, rows: [] } },
    receivables() { return this.receivablesDashboard || { total: 0, rows: [] } },
    net() { return (this.receivables.total || 0) - (this.payables.total || 0) },
    netClass() { return this.net < 0 ? 'bg-danger text-white' : 'bg-light' },
    netLabelClass() { return this.net < 0 ? 'small text-white-50' : 'small text-muted' },

    // Campos de tabla como computed para usar this.money en formatter
    payablesFields() {
      return [
        { key: 'name', label: 'Proveedor' },
        {
          key: 'balance', label: 'Saldo', class: 'text-right', formatter: v => this.money(v),
        },
      ]
    },
    receivablesFields() {
      return [
        { key: 'name', label: 'Cliente' },
        {
          key: 'balance', label: 'Saldo', class: 'text-right', formatter: v => this.money(v),
        },
      ]
    },

    asOfLabel() { return this.formatLong(this.asOf || this.todayIso) },
    todayIso() { return this.toIsoLocal(new Date()) },

    overdueTotal() {
      return { cxc: Number(this.aging.cxc.total || 0), cxp: Number(this.aging.cxp.total || 0) }
    },
    overdueBalance() { return this.overdueTotal.cxc - this.overdueTotal.cxp },

    agingColumns() { return AGING_COLUMNS.map(c => ({ ...c, kind: 'aging' })) },
    weekColumns() {
      return this.upcomingFridays.map(f => ({ key: f, label: this.formatShort(f), kind: 'week' }))
    },
    columns() { return [...this.agingColumns, ...this.weekColumns] },

    rowCxc() { return this.columns.map(c => this.valueAt('cxc', c)) },
    rowCxpNeg() { return this.columns.map(c => -this.valueAt('cxp', c)) },
    rowBalance() { return this.columns.map((c, i) => this.rowCxc[i] + this.rowCxpNeg[i]) },
    rowCumulative() {
      return this.rowBalance.reduce((arr, v) => {
        const last = arr.length ? arr[arr.length - 1] : 0
        arr.push(last + Number(v || 0))
        return arr
      }, [])
    },

    totalCxc() { return this.rowCxc.reduce((a, b) => a + b, 0) },
    totalCxpNeg() { return this.rowCxpNeg.reduce((a, b) => a + b, 0) },
    grandTotal() { return this.totalCxc + this.totalCxpNeg },

    bodyRows() {
      return [
        {
          label: 'Por cobrar',
          values: this.rowCxc,
          subtotal: this.overdueTotal.cxc,
          total: this.totalCxc,
        },
        {
          label: 'Por pagar',
          values: this.rowCxpNeg,
          subtotal: -this.overdueTotal.cxp,
          total: this.totalCxpNeg,
        },
      ]
    },
    footRows() {
      return [
        {
          label: 'Balance',
          values: this.rowBalance,
          subtotal: this.overdueBalance,
          total: this.grandTotal,
        },
        {
          label: 'Acumulado',
          values: this.rowCumulative,
          subtotal: this.overdueBalance,
          total: this.grandTotal,
        },
      ]
    },

    // Gráfica
    lineOptions() {
      return {
        chart: { toolbar: { show: false } },
        stroke: { curve: 'smooth', width: 3 },
        markers: { size: 3 },
        colors: ['#28a745', '#dc3545', '#007bff', '#6f42c1'], // verde, rojo, azul, morado
        xaxis: {
          categories: this.columns.map(c => c.label),
          labels: { rotate: -15 },
        },
        yaxis: {
          labels: { formatter: v => this.money(v) },
          decimalsInFloat: 2,
          tickAmount: 6,
        },
        annotations: {
          xaxis: this.weekColumns.length ? [{
            x: this.weekColumns[0].label,
            borderColor: '#adb5bd',
            label: { text: 'HOY', style: { background: '#adb5bd', color: '#fff' } },
          }] : [],
        },
        grid: { yaxis: { lines: { show: true } } },
        tooltip: { y: { formatter: v => this.money(v) } },
        legend: { position: 'top' },
      }
    },
    lineSeries() {
      return [
        { name: 'Por cobrar', data: this.rowCxc },
        { name: 'Por pagar', data: this.rowCxpNeg },
        { name: 'Balance', data: this.rowBalance },
        { name: 'Acumulado', data: this.rowCumulative },
      ]
    },
  },
  created() { this.load() },
  methods: {
    ...mapActions('finance', [
      'fetchPayablesDashboard',
      'fetchReceivablesDashboard',
      'fetchReceivablesFridayBuckets',
      'fetchPayablesFridayBuckets',
    ]),

    async load() {
      this.loading = true
      try {
        const [payRes, recRes] = await Promise.allSettled([
          this.fetchPayablesDashboard(),
          this.fetchReceivablesDashboard(),
        ])

        const failed = []
        if (payRes.status === 'rejected') failed.push('CxP')
        if (recRes.status === 'rejected') failed.push('CxC')
        this.dashboardError = failed.length
          ? `No se pudieron cargar los totales de ${failed.join(' y ')}. Las cifras están incompletas.`
          : null

        await this.loadBuckets()
      } finally {
        this.loading = false
      }
    },

    valueAt(side, column) {
      if (column.kind === 'aging') return Number(this.aging[side][column.key] || 0)
      return Number(this.upcoming[side][column.key] || 0)
    },

    toIsoLocal(dateObj) {
      const y = dateObj.getFullYear()
      const m = String(dateObj.getMonth() + 1).padStart(2, '0')
      const d = String(dateObj.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    formatShort(iso) {
      const [y, m, d] = iso.split('-').map(n => parseInt(n, 10))
      const dt = new Date(y, m - 1, d)
      return dt.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }).toUpperCase()
    },
    formatLong(iso) {
      const [y, m, d] = String(iso || '').split('-').map(n => parseInt(n, 10))
      if (!y) return '—'
      const dt = new Date(y, m - 1, d)
      return dt.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })
    },

    normalizePayload(payload) {
      const source = payload || {}
      // Un backend anterior no manda overdue_aging: sin esto pintaríamos ceros
      // como si fueran datos reales
      const supported = Object.prototype.hasOwnProperty.call(source, 'overdue_aging')

      const aging = emptyAging()
      Object.entries(source.overdue_aging || {}).forEach(([k, v]) => {
        if (k in aging) aging[k] = Number(v || 0)
      })

      const upcoming = {}
      Object.entries(source.upcoming || {}).forEach(([k, v]) => { upcoming[k] = Number(v || 0) })

      return {
        supported,
        aging,
        upcoming,
        notShown: Number(source.not_shown || 0),
        noDueDate: Number(source.no_due_date || 0),
        asOf: source.as_of || null,
      }
    },

    async loadBuckets() {
      this.requestSeq += 1
      const seq = this.requestSeq
      this.bucketsLoading = true

      const [cxcRes, cxpRes] = await Promise.allSettled([
        this.fetchReceivablesFridayBuckets(),
        this.fetchPayablesFridayBuckets(),
      ])

      // Llegó tarde: ya hay una petición más reciente en curso
      if (seq !== this.requestSeq) return

      this.bucketsLoading = false

      const cxc = this.normalizePayload(cxcRes.status === 'fulfilled' ? cxcRes.value : {})
      const cxp = this.normalizePayload(cxpRes.status === 'fulfilled' ? cxpRes.value : {})

      const failed = []
      if (cxcRes.status === 'rejected') failed.push('CxC')
      if (cxpRes.status === 'rejected') failed.push('CxP')

      const stale = (cxcRes.status === 'fulfilled' && !cxc.supported)
        || (cxpRes.status === 'fulfilled' && !cxp.supported)

      if (failed.length) {
        this.bucketsError = `No se pudo consultar ${failed.join(' ni ')}. Esos importes aparecen en cero y no son reales.`
      } else if (stale) {
        this.bucketsError = 'El servidor todavía no entrega el desglose por antigüedad. Los importes de abajo no son reales; vuelve a intentar cuando termine el despliegue.'
      } else {
        this.bucketsError = null
      }

      this.aging = { cxc: cxc.aging, cxp: cxp.aging }
      this.upcoming = { cxc: cxc.upcoming, cxp: cxp.upcoming }
      this.upcomingFridays = [...new Set([
        ...Object.keys(cxc.upcoming),
        ...Object.keys(cxp.upcoming),
      ])].sort()
      this.notShownTotal = cxc.notShown + cxp.notShown
      this.noDueDateTotal = cxc.noDueDate + cxp.noDueDate
      this.asOf = cxc.asOf || cxp.asOf
      this.lineKey = Date.now()
    },

    money(n) {
      const v = Number(n || 0)
      return `$ ${v.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },

    exportCsv(kind) {
      const list = kind === 'payables' ? this.payables.rows : this.receivables.rows
      const header = 'Nombre,Balance\n'
      const body = (list || [])
        .map(r => `"${String(r.name || '').replace(/"/g, '""')}",${Number(r.balance || 0)}`)
        .join('\n')
      this.downloadCsv(header + body, `${kind}-pivot.csv`)
    },

    exportFridaysCsv() {
      const header = ['Concepto', ...this.agingColumns.map(c => c.label), 'Subtotal vencido',
        ...this.weekColumns.map(c => c.label), 'Total']

      const lines = [...this.bodyRows, ...this.footRows].map(row => ([
        row.label,
        ...this.agingColumns.map((c, i) => row.values[i]),
        row.subtotal,
        ...this.weekColumns.map((c, i) => row.values[this.agingColumns.length + i]),
        row.total,
      ]))

      const toCsv = arr => arr.map(line => line.map(v => (typeof v === 'string' ? `"${v.replace(/"/g, '""')}"` : v)).join(',')).join('\n')

      this.downloadCsv(toCsv([header, ...lines]), `cartera_${this.asOf || this.todayIso}.csv`)
    },

    downloadCsv(content, filename) {
      // BOM para que Excel en es-MX respete los acentos
      const blob = new Blob(['﻿', content], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 0)
    },
  },
}
</script>
